import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'expo-router';
import { useCartContinuePricing } from 'features/cart/hooks/use-cart-continue/use-cart-continue-pricing';
import { useCartValidation } from 'features/cart/hooks/use-cart-validation';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { calculateCartSubTotal, countCartItems } from 'features/cart/utils';
import { logCartCheckoutStarted } from 'features/cart/utils/analytics';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';

import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import useFoodware from 'hooks/use-foodware';
import { routes } from 'navigation/routing';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useSmartAlertsContext } from 'state/smart-alert';
import { isLegacyCondimentsOnlyCart } from 'utils/legacy-condiments';

import { useCartState } from '../../cart-provider';

export const useCartContinue = () => {
  const [, setCartState] = useCartState();
  const { cartEntries, removeItemFromMinimalCart } = useMinimalCart();
  const { store } = useStoreContext();
  const { deliveryAddress, setCheckoutRequestedSubtotalCents } = useOrderContext();
  const serviceMode = useServiceModeLive();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const { navigate } = useRouter();
  const dispatch = useAppDispatch();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { featureFoodware } = useFoodware();
  const { sections: addOnSections } = useFeatureMenuAddOn({ ignoreEnableAddExtras: true });
  const [showInvalidItemsModal, setShowInvalidItemsModal] = useState(false);
  const [showDeliveryMinimumSubtotalModal, setShowDeliveryMinimumSubtotalModal] = useState(false);
  const [showSelectionRequiredModal, setShowSelectionRequiredModal] = useState(false);
  const [showCondimentsOnlyModal, setShowCondimentsOnlyModal] = useState(false);
  const { isStoreAvailable } = useStoreAvailabilityFns();
  const { currentStoreDate } = useSmartAlertsContext();

  const appliedOffersByCartId = useMemo(
    () => new Map(appliedOffers.map(offer => [offer.cartId, offer])),
    [appliedOffers]
  );

  const {
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    hasInvalidEntries,
    hasBlockingOfferErrors,
    isDeliverySubtotalValid,
    isCartTotalValid,
    isCartTotalZeroWithoutReward,
    deliveryMinimumSubtotalCents,
    isMenuReady,
  } = useCartValidation({ cartEntries });

  const hasCartItems = useMemo(() => countCartItems(cartEntries) > 0, [cartEntries]);
  const hasStore = Boolean(store?.number);
  const hasDeliveryAddress = !isDelivery || Boolean(deliveryAddress);
  const foodwareItemIds = useMemo(
    () => (featureFoodware?.items ?? []).flatMap(item => (item?._id ? [item._id] : [])),
    [featureFoodware?.items]
  );
  const isCondimentsOnlyCart = useMemo(
    () => isLegacyCondimentsOnlyCart(cartEntries, addOnSections, foodwareItemIds),
    [addOnSections, cartEntries, foodwareItemIds]
  );
  const isStoreOrderable =
    !!serviceMode && isStoreAvailable(store, serviceMode, currentStoreDate ?? store.localTime);

  const handlePricingSuccess = useCallback(() => {
    setCheckoutRequestedSubtotalCents(calculateCartSubTotal(cartEntries));
    navigate(routes.cartCheckout);
  }, [cartEntries, navigate, setCheckoutRequestedSubtotalCents]);

  const { startPricing, resetPricing, isPricingLoading, pricingError } = useCartContinuePricing({
    cartEntries,
    onSuccess: handlePricingSuccess,
  });

  const hasContinuePrerequisites =
    hasCartItems &&
    hasStore &&
    hasDeliveryAddress &&
    isMenuReady &&
    !isPricingLoading &&
    !pricingError;
  const hasRetryPrerequisites =
    hasCartItems && hasStore && hasDeliveryAddress && isMenuReady && !isPricingLoading;

  const canAttemptPricing =
    !hasBlockingOfferErrors &&
    !hasInvalidEntries &&
    isDeliverySubtotalValid &&
    isCartTotalValid &&
    !isCartTotalZeroWithoutReward;
  const canContinue =
    hasContinuePrerequisites &&
    isStoreOrderable &&
    !hasBlockingOfferErrors &&
    !hasInvalidEntries &&
    isDeliverySubtotalValid &&
    (isCondimentsOnlyCart || canAttemptPricing);
  const canRetryPricing = hasRetryPrerequisites && canAttemptPricing;

  useEffect(() => {
    setCartState(prev =>
      prev.isContinueLoading === isPricingLoading
        ? prev
        : { ...prev, isContinueLoading: isPricingLoading }
    );

    return () => {
      setCartState(prev => (prev.isContinueLoading ? { ...prev, isContinueLoading: false } : prev));
    };
  }, [isPricingLoading, setCartState]);

  const handleContinue = useCallback(() => {
    if (!hasContinuePrerequisites) {
      return;
    }

    if (!isStoreOrderable) {
      return;
    }

    if (isCondimentsOnlyCart) {
      setShowCondimentsOnlyModal(true);
      return;
    }

    if (isCartTotalZeroWithoutReward) {
      setShowSelectionRequiredModal(true);
      return;
    }

    if (!isDeliverySubtotalValid) {
      setShowDeliveryMinimumSubtotalModal(true);
      return;
    }

    if (hasInvalidEntries) {
      setShowInvalidItemsModal(true);
      return;
    }

    if (hasBlockingOfferErrors) {
      return;
    }

    if (!isCartTotalValid) {
      // Fallback for other invalid totals (e.g., negative subtotal).
      setShowSelectionRequiredModal(true);
      return;
    }

    if (serviceMode) {
      logCartCheckoutStarted({
        cartEntries,
        serviceMode,
      });
    }

    startPricing();
  }, [
    hasInvalidEntries,
    hasBlockingOfferErrors,
    hasContinuePrerequisites,
    isCartTotalValid,
    isCartTotalZeroWithoutReward,
    isCondimentsOnlyCart,
    isDeliverySubtotalValid,
    isStoreOrderable,
    cartEntries,
    serviceMode,
    startPricing,
  ]);

  const handleOpenInvalidItemsModal = useCallback(() => {
    // Stale/unavailable cart entries should be surfaced as soon as the user opens cart.
    if (isMenuReady && hasInvalidEntries && !showInvalidItemsModal) {
      setShowInvalidItemsModal(true);
    }
  }, [hasInvalidEntries, isMenuReady, showInvalidItemsModal]);

  const handleInvalidItemsConfirm = useCallback(() => {
    invalidCartEntries.forEach(entry => removeItemFromMinimalCart(entry));
    invalidOfferEntries.forEach(entry => {
      removeItemFromMinimalCart(entry);
      const appliedOffer = appliedOffersByCartId.get(entry.cartId ?? '');
      if (appliedOffer) {
        dispatch(actions.loyalty.removeAppliedOffer(appliedOffer));
      }
    });
    invalidRewardEntries.forEach(entry => removeItemFromMinimalCart(entry));
    setShowInvalidItemsModal(false);
  }, [
    appliedOffersByCartId,
    dispatch,
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    removeItemFromMinimalCart,
  ]);

  const handleTryAgain = useCallback(() => {
    resetPricing();

    if (!canRetryPricing) {
      return;
    }

    startPricing();
  }, [canRetryPricing, resetPricing, startPricing]);

  const handleTryAnotherStore = useCallback(() => {
    navigate(routes.storeLocator);
  }, [navigate]);

  return {
    cartEntries,
    handleContinue,
    handleOpenInvalidItemsModal,
    handleInvalidItemsConfirm,
    handlePricingErrorClose: resetPricing,
    handleDeliveryMinimumSubtotalClose: () => setShowDeliveryMinimumSubtotalModal(false),
    handleCondimentsOnlyClose: () => setShowCondimentsOnlyModal(false),
    handleSelectionRequiredClose: () => setShowSelectionRequiredModal(false),
    handleTryAgain,
    handleTryAnotherStore,
    deliveryMinimumSubtotalCents,
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    isContinueDisabled: !canContinue,
    isPricingLoading,
    pricingError,
    showDeliveryMinimumSubtotalModal,
    showCondimentsOnlyModal,
    showInvalidItemsModal,
    showSelectionRequiredModal,
  } as const;
};
